import React from 'react';
import toast from 'react-hot-toast';
import Modal from '../Modal';
import {
  archiveError,
  useModuleUsage,
  useSetArchived,
} from '../../hooks/useModuleArchive';

const count = (n, one, many = `${one}s`) =>
  `${Number(n).toLocaleString()} ${n === 1 ? one : many}`;

// "A", "A and B", "A, B and C"
const listOf = (items) =>
  items.length < 2
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

function UsageText({ usage }) {
  if (!usage.enrolled_learners) {
    return (
      <p className="text-sm text-gray-700">No learners are enrolled in it.</p>
    );
  }
  return (
    <p className="text-sm text-gray-700">
      {count(usage.enrolled_learners, 'learner is', 'learners are')} enrolled
      {usage.learners_with_progress > 0 &&
        ` (${usage.learners_with_progress.toLocaleString()} started)`}
      . They keep their access and progress.
    </p>
  );
}

function CurriculumWarning({ usage }) {
  const cohorts = usage?.cohorts ?? [];
  const tracks = usage?.specialization_tracks ?? [];
  if (!cohorts.length && !tracks.length) return null;
  const places = [
    cohorts.length &&
      `the curriculum of ${
        cohorts.length === 1 ? 'cohort' : 'cohorts'
      } ${listOf(cohorts)}`,
    tracks.length &&
      `specialization ${tracks.length === 1 ? 'track' : 'tracks'} ${listOf(
        tracks
      )}`,
  ].filter(Boolean);
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
      <p className="text-sm text-amber-800">
        It&apos;s in {listOf(places)}. Archiving hides it from pickers but
        doesn&apos;t remove it there.
      </p>
    </div>
  );
}

export default function ArchiveModuleDialog({ courseId, module, onClose }) {
  const moduleId = module?.id;
  const { data, isLoading, error } = useModuleUsage(moduleId);
  const setArchived = useSetArchived(courseId);
  const usage = data?.usage;
  const name = data?.name || module?.moduleName || 'this module';

  const archive = () =>
    setArchived.mutate(
      { moduleId, archive: true },
      {
        onSuccess: () => {
          toast.success(`${name} is archived.`);
          onClose();
        },
      }
    );

  let usageContent;
  if (isLoading) {
    usageContent = (
      <p className="text-sm text-gray-500">Checking who uses this module…</p>
    );
  } else if (usage) {
    usageContent = (
      <>
        <UsageText usage={usage} />
        <CurriculumWarning usage={usage} />
      </>
    );
  } else {
    usageContent = (
      <p className="text-sm text-gray-700">
        {error
          ? `${archiveError(error, "We couldn't count who uses it.")} `
          : "We couldn't count who uses it just now. "}
        Archiving still works, and enrolled learners keep access.
      </p>
    );
  }

  return (
    <Modal
      modalOpen={Boolean(module)}
      onClose={onClose}
      title={`Archive ${name}?`}
      small
    >
      <div className="space-y-3 text-left">
        {usageContent}
        <p className="text-sm text-gray-700">
          It&apos;s hidden from course lists and module pickers, so nobody new
          joins it. You can unarchive it at any time.
        </p>
        {setArchived.isError && (
          <p className="text-sm text-red-700">
            {archiveError(setArchived.error, "We couldn't archive it.")}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={archive}
            disabled={isLoading || setArchived.isLoading}
            className="rounded-md bg-claret-500 px-4 py-1.5 text-sm text-white hover:bg-claret-400 disabled:opacity-50"
          >
            {setArchived.isLoading ? 'Archiving…' : 'Archive'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
